import { DateTime, Interval, Duration } from 'luxon';
import { LocalTime, RangePreset, ComparePreset } from './datePicker/types';

interface Filter {
  locationId?: string[],
  locationName?: string[],
  installationId?: string,
  installationName?: string,
  campaignId?: string,
  campaignName?: string,
  zone?: string,
  from?: Date,
  to?: Date,
  compareFrom?: Date,
  compareTo?: Date,
  // Corte de día parcial (CX-638 §05) — no nulo solo cuando `to`/`compareTo`
  // alcanzan el día actual. Lo consume QueryWindow.resolveQueryWindow en las
  // queries directas a Firestore (ProductSales.ts, Historical.ts). Las
  // métricas que salen de la API cloud (Audience/Interaction/Occupancy/
  // MediaPlayback/Historical no-sales) NO lo honran — ver el comentario en
  // esos archivos.
  cutoff?: LocalTime | null,
  // Regla que generó compareFrom/compareTo al aplicar el picker (CX-638).
  // Permite reabrir el picker con la regla derivada correcta en vez de caer
  // siempre en 'custom' — ver filterAdapter.pickerStateFromFilter.
  comparePreset?: ComparePreset,
  // Preset que generó from/to (CX-649), para que el picker reabra con él
  // marcado en la barra lateral. Solo se honra si sigue produciendo el mismo
  // rango hoy — ver filterAdapter.pickerStateFromFilter.
  rangePreset?: RangePreset
}

class FilterService {
  static parse(filter: Filter): Filter {
    filter = { ...filter };
    const locationId = (filter as any).locationId;
    if(locationId && !Array.isArray(locationId)) {
      filter.locationId = [locationId];
    }
    if(filter.locationId && !filter.locationId.length) {
      delete filter.locationId;
    }
    const locationName = (filter as any).locationName;
    if(locationName && !Array.isArray(locationName)) {
      filter.locationName = [locationName];
    }
    if(filter.locationName && !filter.locationName.length) {
      delete filter.locationName;
    }
    if(filter.from) {
      filter.from = DateTime.fromJSDate(filter.from).startOf('day').toJSDate();
    }
    if(!filter.to) {
      filter.to = DateTime.now().toJSDate();
    }
    filter.to = DateTime.fromJSDate(filter.to).endOf('day').toJSDate();
    if(filter.compareFrom) {
      filter.compareFrom = DateTime.fromJSDate(filter.compareFrom).startOf('day').toJSDate();
    }
    if(filter.compareTo) {
      filter.compareTo = DateTime.fromJSDate(filter.compareTo).endOf('day').toJSDate();
    }
    return filter;
  }

  static previous(filter: Filter): Filter {
    filter = this.parse(filter);
    if(filter.compareFrom && filter.compareTo) {
      filter.from = filter.compareFrom;
      filter.to = filter.compareTo;
      return filter;
    }
    let from = DateTime.fromJSDate(filter.from || new Date());
    let to = DateTime.fromJSDate(filter.to || new Date());
    const interval = Interval.fromDateTimes(from, to);
    to = from.minus(Duration.fromObject({ days: 1 }));
    const duration = Duration.fromObject({ days: interval.length('days') });
    from = from.minus(duration);
    filter.from = from.startOf('day').toJSDate();
    filter.to = to.endOf('day').toJSDate();
    return filter;
  }

  static getLocationCondition(filter: Filter): { field: string, operator: string, value: any } | undefined {
    const locationIds = filter.locationId || [];
    if(!locationIds.length) {
      return undefined;
    }
    return {
      field: 'locationId',
      operator: locationIds.length === 1 ? '==' : 'in',
      value: locationIds.length === 1 ? locationIds[0] : locationIds
    };
  }

  static getLocationNames(filter: Filter): string {
    return (filter.locationName || []).join(', ');
  }
}

export { Filter, FilterService };
